import React, { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { PersonRef } from "../types";
import {
  PersonListItem,
  getPersonId,
  getPersonName,
  usePeopleList,
} from "../hooks/usePeopleList";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const PAGE_SIZE = 10;

interface PeoplePickerProps {
  title: string;
  corporateId: string | number;
  roleFilter: string;
  initialSelected: PersonRef[];
  onClose: () => void;
  onConfirm: (people: PersonRef[]) => void;
}

const PeoplePicker: React.FC<PeoplePickerProps> = ({
  title,
  corporateId,
  roleFilter,
  initialSelected,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [selected, setSelected] = useState<PersonRef[]>(initialSelected);

  const { items, totalItems, totalPages, isLoading } = usePeopleList({
    corporateId,
    roleFilter,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => String(s.id))),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((p) => getPersonId(p) === String(id));
      if (!found) return;
      setSelected((prev) => [...prev, { id, label: getPersonName(found) }]);
    } else {
      setSelected((prev) => prev.filter((s) => String(s.id) !== String(id)));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 12px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "8px 24px 16px" }}>
        <SearchableMultiSelectListCst<PersonListItem>
          items={items}
          getId={(p) => getPersonId(p)}
          renderRow={({ item }) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {getPersonName(item)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.primary,
                }}
              >
                {item.email || `ID: ${getPersonId(item)}`}
              </Typography>
            </Box>
          )}
          selectedIds={selectedSet}
          onToggle={handleToggle}
          initialSearchValue={search}
          onSearchSubmit={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder={String(t("people-picker.search-placeholder"))}
          searchButtonLabel={String(t("people-picker.search-button"))}
          hideSearch
          headerSlot={
            <FiltersAccordionToggle
              pageId={PagesID["corporate.editions.tutors-faculties"]}
              value={fieldFilters}
              onApply={(next) => {
                setFieldFilters(next);
                setPage(1);
              }}
            />
          }
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          isLoading={isLoading}
          selectedCountLabel={String(
            t("people-picker.selected-count", { count: selected.length }),
          )}
          emptyLabel={String(t("people-picker.empty"))}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 20px" }}>
        <ButtonSecondary size="md" onClick={onClose}>
          {t("actions.cancel")}
        </ButtonSecondary>
        <ButtonPrimary
          size="md"
          onClick={() => {
            onConfirm(selected);
            onClose();
          }}
        >
          {t("actions.confirm")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default PeoplePicker;
